import {
  defaultShouldDehydrateQuery,
  environmentManager,
  QueryCache,
  QueryClient,
  type QueryClientConfig,
} from '@tanstack/react-query';

export const EVENT_SYNC_ERROR = '@svenskaspelab/poolbetting/core/syncError';

export type ErrorPayload = {
  title?: string;
  message: string;
  internalMessage?: string;
};

/**
 * Checks whether a dispatched payload is of type ErrorPayload.
 *
 * @param value Value to check
 * @returns True if payload is ErrorPayload
 */
export function isErrorPayload(value: unknown): value is ErrorPayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  if (!('message' in value) || typeof value.message !== 'string') {
    return false;
  }

  if ('title' in value && value.title !== undefined && typeof value !== 'string') {
    return false;
  }

  if ('internalMessage' in value && value.internalMessage !== undefined && typeof value !== 'string') {
    return false;
  }

  return true;
}

function makeQueryClient(config?: QueryClientConfig): QueryClient {
  return new QueryClient({
    // Tanstack Query uses "aggressive but sane" refetch technique, read more about the defaults:
    // https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults?from=reactQueryV3
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
      dehydrate: {
        // include pending queries in dehydration
        // so we can send await pending queries on the client
        // utilizing useSuspenseQuery
        // See TanStack docs for more info:
        // https://tanstack.com/query/v5/docs/framework/react/guides/advanced-ssr#streaming-with-server-components
        // Errored queries must be included as well, otherwise a failed server prefetch is dropped from the
        // dehydrated state and useSuspenseQuery calls the server action during the initial render, which
        // Next.js rejects with "Server Functions cannot be called during initial render".
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) || query.state.status === 'pending' || query.state.status === 'error',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        shouldRedactErrors: (error): boolean => {
          // We should not catch Next.js server errors
          // as that's how Next.js detects dynamic pages
          // so we cannot redact them.
          // Next.js also automatically redacts errors for us
          // with better digests.
          return false;
        },
      },
    },
    ...config,
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

/**
 * Creates a new QueryClient on the server, or reuses an existing one on the client (browser).
 *
 * @returns Tanstack QueryClient instance
 */
export function getQueryClient(): QueryClient {
  if (environmentManager.isServer()) {
    // Server: always make a new query client for every call to getQueryClient.
    // If desired, we can use React.cache to only create one QueryClient per request. This can be achieved by adding the following code to the root of this file
    // const getQueryClient = cache(() => new QueryClient()) // this won't leak data between requests
    // The downside of reusing the QueryClient is that the data will all be serialized every time it is dehydrated to a client component.
    return makeQueryClient();
  }

  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient({
      defaultOptions: {
        queries: {
          // Globally disable refetch to avoid refetching data if a new component queries data since tanstack considers data to be stale immediately.
          // This is important for opened draw so they do not diverge from the server components in nextjs.
          // The cache can be manually updated via the refetch function returned from useQuery
          // Updates will be done via socket for most of the data that is considered 'live'.
          // If the behavelour should be different for a certain API it can be overridden by leveraging the query keys factory by
          // eg: `browserQueryClient.setQueryDefaults(drawQueryKeys.opened(), {/* options goes here */});
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          staleTime: Infinity,
        },
      },
      queryCache: new QueryCache({
        onError: (error, query): void => {
          // Internal hooks may dispatch the 'meta' property on queried to enable MFE to display it to the user.
          // The errors may be consumed from the MFE via useOnSyncError hook.
          if (isErrorPayload(query.meta?.['errorMessage'])) {
            const event = new CustomEvent(EVENT_SYNC_ERROR, {
              detail: query.meta['errorMessage'],
            });
            window.dispatchEvent(event);
          }
        },
      }),
    });
  }

  return browserQueryClient;
}
